import React, { useState, useRef } from 'react';
import { FaEye, FaEyeSlash, FaGoogle, FaLinkedin } from 'react-icons/fa';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import { loginUser } from '../../services/authentication-services';
import Loader from '../../components/Loader/Loader';
import { Toaster } from 'react-hot-toast';
import { notify } from '../../utilities/toast';
import { useGoogleLogin } from '@react-oauth/google';
import { useTranslation } from 'react-i18next';
import { config } from '../../config/config';

// Add these interfaces at the top of the file
interface LoginData {
  username: string;
  email: string;
  password: string;
}

interface ApiResponse {
  data?: {
    access?: string;
    email?: string;
    mfa_required?: boolean;
    role?: string;
    user?: {
      email: string;
      username: string;
      first_name: string;
      last_name: string;
      phone: string;
      profile_picture: string;
    };
  };
  status: number;
  error?: string;
  detail?: string;
}

interface GoogleErrorResponse {
  response?: {
    data: {
      error: string;
    };
  };
}

const Login = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginData>();
  //const recaptchaRef = React.createRef();

  //const onSubmit = async (data: LoginData) => {
  // #TODO activate captcha
  // const captchaValue = recaptchaRef.current.getValue();
  // if (!captchaValue) {
  //   notify(t('login.verifyRecaptcha'), "error");
  // } else {
  //   setLoading(true);
  //   const response = await loginUser(
  //     {
  //       username: data.email,
  //       password: data.password,
  //     },
  //     setLoading
  //   );
  //   console.log("response", response);
  //   if (response.status == 400) {
  //     notify(response.error, "error");
  //   }
  //   if (response?.data?.mfa_required && response?.data.mfa_required == true) {
  //     localStorage.setItem("email", response.data.email);
  //     navigate("/verify-mfa");
  //   }
  //   if (response?.data?.access) {
  //     localStorage.setItem("token", response.data.access);
  //     notify("login succesfull", "success");

  //     setTimeout(() => {
  //       navigate("/profile");
  //     }, 4000);
  //   }
  // }
  /*
      const captchaValue = recaptchaRef.current.getValue();
      if (!captchaValue) {
        notify(t('login.verifyRecaptcha'), "error");
      } else {*/

  //const captchaValue = recaptchaRef.current?.getValue();

  // Only check captcha in production
  //const captchaValue = config.ENABLE_CAPTCHA ? recaptchaRef.current?.getValue() : true;
  //
  //// Check if captcha is enabled and verify it
  //if (config.ENABLE_CAPTCHA && !captchaValue) {
  //  notify(t('login.verifyRecaptcha'), "error");
  //  return;
  //}

  const onSubmit = async (data: LoginData) => {
    try {
      // Only check captcha in production
      //console.log('Form submission data:', {
      //  ...data,
      //  password: '[REDACTED]'
      //});

      const captchaValue = config.ENABLE_CAPTCHA
        ? recaptchaRef.current?.getValue()
        : true;
      //console.log('Captcha value:', !!captchaValue);

      // Check if captcha is enabled and verify it
      if (config.ENABLE_CAPTCHA && !captchaValue) {
        notify(t('login.verifyRecaptcha'), 'error');
        return;
      }

      setLoading(true);
      const response = await loginUser(
        {
          username: data.email,
          password: data.password,
        },
        setLoading
      );

      //console.log('Login response:', {
      //  status: response?.status,
      //  hasData: !!response?.data,
      //  error: response?.error,
      //});

      if (response?.mfa_required) {
        // Handle MFA case
        localStorage.setItem('email', response.email || '');
        localStorage.setItem('username', response.username || '');
        localStorage.setItem('firstname', response.first_name || '');
        localStorage.setItem('lastname', response.last_name || '');
        notify(t('login.mfaRequired'), 'info');
        navigate('/verify-mfa');
        return;
      }

      // Handle normal login (no MFA)
      if (response?.access) {
        localStorage.setItem('token', response.access);
        localStorage.setItem('email', response.user?.email || '');
        localStorage.setItem('username', response.user?.username || '');
        localStorage.setItem('firstname', response.user?.first_name || '');
        localStorage.setItem('lastname', response.user?.last_name || '');

        notify(t('login.loginSuccessful'), 'success');

        setTimeout(() => {
          if (response.user?.role === 'ADMIN') {
            navigate('/admin/dashboard');
          } else {
            navigate('/profile');
          }
        }, 4000);
        return;
      }

      // Handle error cases
      if (response?.error || response?.detail) {
        notify(
          response.error || response.detail || t('login.invalidCredentials'),
          'error'
        );
        return;
      }
    } catch (error) {
      console.error('Login error:', error);
      notify(t('login.serverError'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const serverUrl = config.BACKEND_SERVER_URL;

  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        const response = await axios.post<ApiResponse>(
          `${serverUrl}/auth/google/`,
          {
            access_token: tokenResponse.access_token,
          }
        );

        if (response.status === 200 && response.data) {
          const { user } = response.data.data;
          localStorage.setItem('email', user.email);
          localStorage.setItem('username', user.username);
          localStorage.setItem('firstname', user.first_name);
          localStorage.setItem('lastname', user.last_name);
          localStorage.setItem('phone', user.phone);
          localStorage.setItem('token', response.data.access || '');
          localStorage.setItem('profilepic', user.profile_picture);

          navigate('/profile');
          return { data: response.data, status: response.status };
        } else {
          // console.log(response, "reesponse");
          throw response?.error;
        }
      } catch (error) {
        const googleError = error as GoogleErrorResponse;
        //console.error('Google login failure:', googleError.response?.data);
        notify(
          googleError.response?.data.error || 'Unable to login with Google',
          'error'
        );
      }
    },
    onError: (error) => {
      //console.log(error);
      //console.error('Google OAuth error:', error);
      notify('Google login failed. Please try again later.', 'error');
    },
    scope: 'openid profile email',
  });

  return (
    <div className="dark:bg-black min-h-screen flex items-start justify-center py-4 pt-24 lg:py-24 px-4 sm:px-6 lg:px-8 bg-gray-100">
      <div
        className={`w-full max-w-lg  dark:bg-slate-900 bg-white p-8 rounded-lg shadow-lg  `}
      >
        <h2 className="dark:text-white text-3xl font-semibold text-gray-900 mb-6">
          {t('login.welcomeBack')}
        </h2>
        <p className="dark:text-zinc-400 text-gray-700 mb-6">
          {t('login.continueMessage')}
        </p>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <label
            htmlFor="email"
            className="dark:text-zinc-400 block text-sm font-medium text-gray-700"
          >
            {t('login.emailUsername')}
          </label>
          <div>
            <input
              {...register('email', {
                required: t('login.emailRequired'),
              })}
              id="email"
              placeholder={t('login.emailPlaceholder')}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 bg-gray-50 text-gray-900 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-500"
            />
            {errors.email && (
              <p className="text-red-500 text-sm">{errors.email.message}</p>
            )}
          </div>

          <label
            htmlFor="password"
            className="dark:text-zinc-400 block text-sm font-medium text-gray-700"
          >
            {t('login.password')}
          </label>
          <div className="relative flex flex-col">
            <input
              {...register('password', {
                required: t('login.passwordRequired'),
                minLength: {
                  value: 6,
                  message: t('login.passwordLength'),
                },
              })}
              type={showPassword ? 'text' : 'password'}
              id="password"
              placeholder={t('login.passwordPlaceholder')}
              className="w-full px-3 py-2 pr-10 border border-gray-300 bg-gray-50 text-gray-900 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-500"
            />
            {showPassword ? (
              <FaEye
                className={`text-gray-500 absolute cursor-pointer right-4 top-3`}
                onClick={() => setShowPassword(!showPassword)}
              />
            ) : (
              <FaEyeSlash
                className={`text-gray-500 absolute cursor-pointer right-4 top-3`}
                onClick={() => setShowPassword(!showPassword)}
              />
            )}
            {errors.password && (
              <p className="text-red-500 text-sm">{errors.password.message}</p>
            )}
          </div>

          {config.ENABLE_CAPTCHA && (
            <div className="flex items-center justify-center m-8">
              <ReCAPTCHA sitekey={config.SITE_KEY} ref={recaptchaRef} />
            </div>
          )}

          <div>
            <button
              type="submit"
              className="w-full px-4 py-2 bg-gray-800 text-white font-semibold rounded-lg shadow-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-600"
            >
              {loading ? <Loader color="white" /> : t('login.loginButton')}
            </button>
          </div>

          <div className="text-sm text-right">
            <Link
              to="/forgot-password"
              className="dark:text-zinc-400 text-gray-500 hover:text-gray-600"
            >
              {t('login.forgotPassword')}
            </Link>
          </div>
        </form>

        <div className="my-6 text-center">
          <p className="text-gray-500">{t('login.orLoginWith')}</p>
          <div className="flex justify-center space-x-4 mt-4">
            <button
              type="button"
              onClick={() => {
                googleLogin();
              }}
              className="flex items-center space-x-2 bg-red-600 text-white px-4 py-2 rounded-lg shadow-md hover:bg-red-700 focus:outline-none"
            >
              <FaGoogle className="text-xl" />
              <span>{t('login.google')}</span>
            </button>
            <NavLink
              to={config.LINKEDIN_LOGIN_URL}
              className="flex items-center justify-center space-x-2 bg-blue-700 text-white px-4 py-2 rounded-lg shadow-md hover:bg-blue-800 focus:outline-none transition"
            >
              <FaLinkedin className="text-lg sm:text-xl" />
              <span className="text-sm sm:text-base">
                {t('login.linkedin')}
              </span>
            </NavLink>
          </div>
        </div>

        <div className="mt-6 text-center">
          <p className="dark:text-zinc-400 text-gray-500">
            {t('login.noAccount')}{' '}
            <Link
              to={'/signup'}
              className="dark:text-white text-gray-900 underline"
            >
              {t('login.signUp')}
            </Link>
          </p>
        </div>
      </div>
      <Toaster />
    </div>
  );
};

export default Login;
